import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Textarea,
  Select,
  VStack,
  HStack,
  useToast,
  Text,
  Box,
  Heading,
  SimpleGrid,
  Icon,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { User } from '../../types';
import { TrendingUp, Sparkles } from 'lucide-react';

interface WinExcitedModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserId?: string;
  users: User[];
  onSave: (
    type: 'win' | 'excited',
    content: string,
    taggedUserId: string | null,
    currentUserId: string
  ) => Promise<void>;
}

type ModalStep = 'selection' | 'form';

/**
 * Win/Excited Modal Component
 * 2-step wizard to add wins or excited items.
 */
export function WinExcitedModal({
  isOpen,
  onClose,
  currentUserId,
  users,
  onSave,
}: WinExcitedModalProps) {
  const [step, setStep] = useState<ModalStep>('selection');
  const [type, setType] = useState<'win' | 'excited'>('win');
  const toast = useToast();

  // Reset to step 1 when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => setStep('selection'), 300); // Delay to allow close animation
    }
  }, [isOpen]);

  const validationSchema = Yup.object({
    content: Yup.string()
      .min(1, 'Content is required')
      .max(500, 'Content must be 500 characters or less')
      .required('Content is required'),
    taggedUserId: Yup.string().nullable(),
  });

  const handleSubmit = async (values: { content: string; taggedUserId: string }) => {
    if (!currentUserId) {
      toast({
        title: 'Error',
        description: 'You must be logged in.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await onSave(type, values.content, values.taggedUserId || null, currentUserId);
      toast({
        title: 'Success',
        description: `Thank you for sharing your ${type === 'win' ? 'win' : 'excitement'}! We appreciate you taking the time to celebrate with the team.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSelectType = (selectedType: 'win' | 'excited') => {
    setType(selectedType);
    setStep('form');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        {step === 'selection' ? (
          <>
            <ModalHeader>
              <Heading size="lg" textAlign="center" mt={4}>
                Share Your News!
              </Heading>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody py={6} px={8}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {/* Win Card */}
                <Box
                  as="button"
                  p={8}
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="lg"
                  textAlign="center"
                  onClick={() => handleSelectType('win')}
                  _hover={{
                    shadow: 'md',
                    borderColor: 'green.300',
                    bg: 'green.50',
                  }}
                  transition="all 0.2s ease-in-out"
                >
                  <VStack spacing={4}>
                    <Icon
                      as={TrendingUp}
                      w={12}
                      h={12}
                      color="green.500"
                      p={2}
                      bg="green.100"
                      borderRadius="full"
                    />
                    <Heading size="md">Win</Heading>
                    <Text fontSize="sm" color="gray.600">
                      Share a victory or accomplishment
                    </Text>
                  </VStack>
                </Box>
                {/* Excited Card */}
                <Box
                  as="button"
                  p={8}
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="lg"
                  textAlign="center"
                  onClick={() => handleSelectType('excited')}
                  _hover={{
                    shadow: 'md',
                    borderColor: 'blue.300',
                    bg: 'blue.50',
                  }}
                  transition="all 0.2s ease-in-out"
                >
                  <VStack spacing={4}>
                    <Icon
                      as={Sparkles}
                      w={12}
                      h={12}
                      color="blue.500"
                      p={2}
                      bg="blue.100"
                      borderRadius="full"
                    />
                    <Heading size="md">Excited</Heading>
                    <Text fontSize="sm" color="gray.600">
                      Share what you're looking forward to
                    </Text>
                  </VStack>
                </Box>
              </SimpleGrid>
            </ModalBody>
          </>
        ) : (
          <Formik
            initialValues={{ content: '', taggedUserId: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values }) => (
              <Form>
                <ModalHeader>
                  <HStack>
                    <Icon
                      as={type === 'win' ? TrendingUp : Sparkles}
                      w={6}
                      h={6}
                      color={type === 'win' ? 'green.500' : 'blue.500'}
                    />
                    <Heading size="md">
                      {type === 'win'
                        ? 'Share Your Win'
                        : 'Share Your Excitement'}
                    </Heading>
                  </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <VStack spacing={4} align="stretch">
                    <Field name="content">
                      {({ field, meta }: any) => (
                        <FormControl isInvalid={meta.error && meta.touched}>
                          <Textarea
                            {...field}
                            placeholder={
                              type === 'win'
                                ? 'What was your victory?'
                                : 'What are you excited about?'
                            }
                            rows={5}
                          />
                          <FormErrorMessage>{meta.error}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    <Field name="taggedUserId">
                      {({ field, meta }: any) => (
                        <FormControl isInvalid={meta.error && meta.touched}>
                          <FormLabel fontSize="sm" color="gray.700">
                            Tag a team member (optional)
                          </FormLabel>
                          <Select {...field} placeholder="No one">
                            {users.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.name}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{meta.error}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </VStack>
                </ModalBody>
                <ModalFooter>
                  <Button
                    variant="ghost"
                    mr={3}
                    onClick={() => setStep('selection')}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    colorScheme="blue"
                    isLoading={isSubmitting}
                    loadingText="Saving..."
                  >
                    Save
                  </Button>
                </ModalFooter>
              </Form>
            )}
          </Formik>
        )}
      </ModalContent>
    </Modal>
  );
}